/**
 * ChartControls - Chart Control Panel Component
 * 
 * SOLID Principles: Single Responsibility Principle (SRP) & Interface Segregation Principle (ISP)
 * Purpose: This component is solely responsible for rendering chart control
 * UI elements (buttons, toggles, etc.). It focuses only on user interface
 * presentation and delegates all logic to appropriate services.
 * 
 * Responsibilities:
 * - Render control buttons (zoom reset, toggle data points, etc.)
 * - Handle user interactions and pass to appropriate handlers
 * - Display current chart state (zoom level, data point count, etc.)
 * - Provide accessible and consistent UI controls
 * 
 * Context: Chart needs various controls for user interaction - zoom reset,
 * toggle data point visibility, export functions, etc. This component
 * provides a clean interface for these controls.
 */

import React from 'react';

/**
 * Main chart controls component
 * 
 * @param {Object} props
 * @param {boolean} props.showDots - Whether data points are currently visible
 * @param {Function} props.onToggleDots - Handler for toggling data point visibility
 * @param {Function} props.onResetZoom - Handler for zoom reset
 * @param {boolean} props.isZoomed - Whether chart is currently zoomed
 * @param {boolean} props.showDirections - Whether directional arrows are currently visible
 * @param {Function} props.onToggleDirections - Handler for toggling directional arrow visibility
 * @param {boolean} props.hasDirectionalData - Whether directional data is available to show
 */
export function ChartControls({
  showDots = false,
  onToggleDots,
  onResetZoom,
  isZoomed = false,
  showDirections = false,
  onToggleDirections,
  hasDirectionalData = false
}) {
  return (
    <ControlsContainer>
      <ControlsSection>
        <DataPointToggle
          showDots={showDots}
          onToggle={onToggleDots}
        />
        
        {hasDirectionalData && (
          <DirectionToggle
            showDirections={showDirections}
            onToggle={onToggleDirections}
          />
        )}
        
        <ZoomResetButton
          onReset={onResetZoom}
          isZoomed={isZoomed}
        />
      </ControlsSection>
    </ControlsContainer>
  );
}

/**
 * Toggle button for data point visibility
 * 
 * @param {Object} props
 * @param {boolean} props.showDots - Current visibility state
 * @param {Function} props.onToggle - Toggle handler
 */
function DataPointToggle({ showDots, onToggle }) {
  return (
    <ControlButton
      onClick={() => onToggle(!showDots)}
      active={showDots}
      title={showDots ? "Hide individual data points" : "Show individual data points"}
    >
      {showDots ? "Hide Data Points" : "Show Data Points"}
    </ControlButton>
  );
}

/**
 * Toggle button for directional arrow visibility
 * 
 * @param {Object} props
 * @param {boolean} props.showDirections - Current visibility state
 * @param {Function} props.onToggle - Toggle handler
 */
function DirectionToggle({ showDirections, onToggle }) {
  return (
    <ControlButton
      onClick={() => onToggle(!showDirections)}
      active={showDirections}
      title={showDirections ? "Hide directional arrows" : "Show directional arrows"}
    >
      {showDirections ? "Hide Directions" : "Show Directions"}
    </ControlButton>
  );
}

/**
 * Zoom reset button
 * 
 * @param {Object} props
 * @param {Function} props.onReset - Reset handler
 * @param {boolean} props.isZoomed - Whether chart is zoomed
 */
function ZoomResetButton({ onReset, isZoomed }) {
  return (
    <ControlButton
      onClick={onReset}
      disabled={!isZoomed}
      title={isZoomed ? "Reset to full time range" : "Chart is not zoomed"}
    >
      Reset Zoom
    </ControlButton>
  );
}


/**
 * Data status display showing current view information
 * 
 * @param {Object} props
 * @param {number} props.dataPointCount - Points in current view
 * @param {number} props.totalDataPoints - Total points in dataset
 * @param {number} props.zoomLevel - Zoom percentage
 * @param {boolean} props.isZoomed - Whether zoomed
 */
function DataStatus({ dataPointCount, totalDataPoints, zoomLevel, isZoomed }) {
  const getStatusText = () => {
    if (!isZoomed) {
      return `${totalDataPoints} data points`;
    }
    
    return `${dataPointCount} of ${totalDataPoints} points (${zoomLevel}% view)`;
  };

  const getStatusColor = () => {
    if (dataPointCount === 0) return '#dc3545'; // Red for no data
    if (dataPointCount < 10) return '#ffc107';   // Yellow for sparse data
    return '#6c757d'; // Gray for normal
  };

  return (
    <StatusDisplay style={{ color: getStatusColor() }}>
      {getStatusText()}
    </StatusDisplay>
  );
}

/**
 * Reusable control button component
 * 
 * @param {Object} props
 * @param {Function} props.onClick - Click handler
 * @param {boolean} props.active - Whether button is in active state
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {string} props.title - Tooltip text
 * @param {React.ReactNode} props.children - Button content
 */
function ControlButton({ 
  onClick, 
  active = false, 
  disabled = false, 
  title, 
  children 
}) {
  const getButtonStyle = () => {
    const baseStyle = {
      padding: '0.5rem 1rem',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: '0.9rem',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      opacity: disabled ? 0.6 : 1
    };

    if (active) {
      return {
        ...baseStyle,
        background: '#007bff',
        color: 'white',
        borderColor: '#007bff'
      };
    }

    return {
      ...baseStyle,
      background: 'white',
      color: '#495057',
      ':hover': !disabled ? {
        background: '#f8f9fa',
        borderColor: '#adb5bd'
      } : {}
    };
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={getButtonStyle()}
    >
      {children}
    </button>
  );
}

/**
 * Container for all chart controls
 */
function ControlsContainer({ children }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      margin: '0 0 1rem 0'
    }}>
      {children}
    </div>
  );
}

/**
 * Section for primary control buttons
 */
function ControlsSection({ children }) {
  return (
    <div style={{
      display: 'flex',
      gap: '1rem',
      alignItems: 'center'
    }}>
      {children}
    </div>
  );
}

/**
 * Section for status information
 */
function StatusSection({ children }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.85rem'
    }}>
      {children}
    </div>
  );
}

/**
 * Status display component
 */
function StatusDisplay({ children, style }) {
  return (
    <span style={{
      fontFamily: 'monospace',
      fontWeight: '500',
      ...style
    }}>
      {children}
    </span>
  );
}

export default ChartControls;